import React, { useContext, useMemo, useState } from "react";
import { FiArrowRight, FiCheck, FiShoppingBag, FiTrash2, FiTruck } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";
import QuantitySelector from "./QuantitySelector";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeCart, updateQuantity, clearCart } = useContext(CartContext);
  const [promo, setPromo] = useState("");
  const [promoMessage, setPromoMessage] = useState("");

  const lines = useMemo(
    () => cartItems
      .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
      .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = siteConfig.delivery.standardFee;

  const applyPromo = (event) => {
    event.preventDefault();
    setPromoMessage(
      promo.trim().toUpperCase() === "HELLOCLUB"
        ? "Code saved — promotional pricing is already reflected in your total."
        : "That code is not active. Try HELLOCLUB."
    );
  };

  if (!lines.length) {
    return (
      <main className="cart-page cart-page--empty">
        <PageMeta title="Your cart" />
        <div className="empty-state-card">
          <span className="empty-state-icon"><FiShoppingBag /></span>
          <p className="eyebrow">Your chilled bag</p>
          <h1>Nothing delicious in here yet.</h1>
          <p>Build a board, stock the fridge or discover this month’s producer selection.</p>
          <Link to="/shop" className="button button--primary">Explore the shop <FiArrowRight /></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <PageMeta title="Your cart" />
      <div className="container cart-page__header">
        <div><p className="eyebrow">Your chilled bag</p><h1>Shopping cart</h1></div>
        <button type="button" onClick={clearCart}>Clear cart</button>
      </div>

      <div className="container cart-layout">
        <section className="cart-lines" aria-label="Cart items">
          <div className="delivery-progress"><FiTruck /><div><p><strong>Carefully packed delivery · {formatPrice(shipping)}</strong></p></div></div>

          {lines.map((item) => (
            <article className="cart-line" key={item.lineKey}>
              <Link className="cart-line__image" to={`/shopDetails/${item.product.id}`}>
                <img src={item.product.image} alt={item.product.name} />
              </Link>
              <div className="cart-line__main">
                <p className="eyebrow">{item.product.subcategory}</p>
                <h2><Link to={`/shopDetails/${item.product.id}`}>{item.product.name}</Link></h2>
                <p>{item.option || item.product.unit} · {item.product.attributes.origin}</p>
                <div className="cart-line__mobile-price">{formatPrice(item.product.price * item.quantity)}</div>
                <div className="cart-line__controls">
                  <QuantitySelector
                    value={item.quantity}
                    onChange={(quantity) => updateQuantity(item.id, item.option, quantity)}
                  />
                  <button type="button" className="remove-line" onClick={() => removeCart(item.id, item.option)}><FiTrash2 /> Remove</button>
                </div>
              </div>
              <div className="cart-line__price">
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                <small>{formatPrice(item.product.price)} each</small>
              </div>
            </article>
          ))}
          <Link className="text-link cart-continue" to="/shop">← Continue shopping</Link>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <div className="summary-row"><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></div>
          <div className="summary-row"><span>Chilled delivery</span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong></div>
          <p className="summary-note">Any applicable taxes are shown in the final total.</p>
          <div className="summary-total"><span>Total</span><strong>{formatPrice(subtotal + shipping)}</strong></div>

          <form className="promo-form" onSubmit={applyPromo}>
            <label htmlFor="promo">Promo code</label>
            <div><input id="promo" value={promo} onChange={(event) => setPromo(event.target.value)} placeholder="Enter code" /><button type="submit">Apply</button></div>
            {promoMessage && <p role="status">{promoMessage}</p>}
          </form>
          <button className="button button--primary checkout-button" type="button" onClick={() => navigate("/checkout")}>
            Secure checkout <FiArrowRight />
          </button>
          <div className="summary-reassurance"><FiCheck /> Secure payment is completed by our payment provider.</div>
        </aside>
      </div>

      <section className="cart-recommendations section-space">
        <div className="container">
          <div className="section-heading"><div><p className="eyebrow">Complete the board</p><h2>A few good companions</h2></div></div>
          <div className="product-grid product-grid--four">
            {productData.filter((item) => [20, 22, 24, 27].includes(item.id)).map((item) => <ProductCard key={item.id} product={item} />)}
          </div>
        </div>
      </section>
    </main>
  );
};

export default CartPage;
